import * as z from "zod";

import { DrawPolygonMode, ModifyMode, MultiPolygon, Polygon, ViewMode } from "@deck.gl-community/editable-layers";
import {
  FreeSessionWindowTypeEnum,
  PaidDayOfWeek,
  PaidTimeRange,
  ParkingPaymentModeType,
  PaymentZoneArea,
  ProviderParking,
  TicketProviderEnum,
  RightsProviderEnum,
} from "@/modules/parking-payment/payment-zone-area.type";

import { DraftState } from "./hooks/use-payment-zone-creation";
import { AnchorUnitEnum, GenerationModeEnum, RateTypeEnum, RoundModeEnum } from "./pricing-configurator.types";

import { ParkingMeter } from "services/web/api-parking-meter.type";
import { toast } from "sonner";

type PaymentModeConstructor = typeof ViewMode | typeof DrawPolygonMode | typeof ModifyMode;

export const initParkingMeter: ParkingMeter = {
  name: "",
  address: {
    streetNumber: "",
    streetName: "",
    city: "",
    postalCode: "",
    country: "",
  },
  status: "",
  externalId: "",
  location: {
    type: "Point",
    coordinates: [],
  },
  operator: "",
  brand: "",
  model: "",
  zone: "",
  options: {
    cash: false,
    creditCard: false,
    contactless: false,
  },
};

export enum ParkingMeterModalState {
  SHOW = "SHOW",
  DEACTIVATE = "DEACTIVATE",
  MINIMIZE = "MINIMIZE",
}

export const parkingMeterSchema = z.object({
  name: z.string().min(1, "Name is required"),
  address: z.object({
    streetNumber: z.string().min(1, "Street number is required"),
    streetName: z.string().min(1, "Street name is required"),
    city: z.string().min(1, "City is required"),
    province: z.string().optional().nullable(),
    postalCode: z.string().min(1, "Postal code is required"),
    country: z.string().min(1, "Country is required"),
  }),
  status: z.string().optional(),
  externalId: z.string().optional(),
  operator: z.string().min(1, "Operator is required"),
  brand: z.string().min(1, "Brand is required"),
  model: z.string().min(1, "Model is required"),
  zone: z.string().optional(),
  batteryLevel: z.number().min(0, "Battery level must be a positive number").optional(),
  lastMaintenanceDate: z.date().optional(),
  options: z
    .object({
      cash: z.boolean().optional(),
      creditCard: z.boolean().optional(),
      contactless: z.boolean().optional(),
    })
    .optional(),
});

export type ParkingMeterFormValues = z.infer<typeof parkingMeterSchema>;

const localizedStringSchema = z.object({
  locale: z.string(),
  text: z.string(),
});

const multilingualFieldSchema = z
  .array(localizedStringSchema)
  .min(1, "Au moins une traduction est requise")
  .refine(
    (items) => {
      const locales = items.map((item) => item.locale);
      return new Set(locales).size === locales.length;
    },
    { message: "Les locales doivent être uniques" },
  );

const pricingPointSchema = z.object({
  id: z.string().optional(),
  duration: z.number().int().min(0),
  unit: z.enum(AnchorUnitEnum).optional(),
  priceAmount: z.number().min(0),
  isShortcut: z.boolean().optional(),
});

const generationModeSchema = z.object({
  mode: z.enum(GenerationModeEnum),
  stepValue: z.number().optional(),
});

const roundingRulesSchema = z.object({
  roundTo: z.number().positive(),
  roundMode: z.enum(RoundModeEnum),
  keepAnchorsExact: z.boolean(),
});

export const MONTHS_OF_YEAR: { num: number; label: string }[] = [
  { num: 1, label: "Janv." },
  { num: 2, label: "Févr." },
  { num: 3, label: "Mars" },
  { num: 4, label: "Avr." },
  { num: 5, label: "Mai" },
  { num: 6, label: "Juin" },
  { num: 7, label: "Juill." },
  { num: 8, label: "Août" },
  { num: 9, label: "Sept." },
  { num: 10, label: "Oct." },
  { num: 11, label: "Nov." },
  { num: 12, label: "Déc." },
];

export const DAYS_OF_WEEK: { value: PaidDayOfWeek; label: string }[] = [
  { value: PaidDayOfWeek.Monday, label: "Lundi" },
  { value: PaidDayOfWeek.Tuesday, label: "Mardi" },
  { value: PaidDayOfWeek.Wednesday, label: "Mercredi" },
  { value: PaidDayOfWeek.Thursday, label: "Jeudi" },
  { value: PaidDayOfWeek.Friday, label: "Vendredi" },
  { value: PaidDayOfWeek.Saturday, label: "Samedi" },
  { value: PaidDayOfWeek.Sunday, label: "Dimanche" },
];

const makeRanges = (days: PaidDayOfWeek[]): PaidTimeRange[] =>
  days.map((day) => ({ day, startTime: "09:00", endTime: "19:00" }));

export const PAID_TIME_RANGE_SHORTCUTS: { label: string; ranges: PaidTimeRange[] }[] = [
  {
    label: "Lun–Ven",
    ranges: makeRanges([
      PaidDayOfWeek.Monday,
      PaidDayOfWeek.Tuesday,
      PaidDayOfWeek.Wednesday,
      PaidDayOfWeek.Thursday,
      PaidDayOfWeek.Friday,
    ]),
  },
  {
    label: "Lun–Sam",
    ranges: makeRanges([
      PaidDayOfWeek.Monday,
      PaidDayOfWeek.Tuesday,
      PaidDayOfWeek.Wednesday,
      PaidDayOfWeek.Thursday,
      PaidDayOfWeek.Friday,
      PaidDayOfWeek.Saturday,
    ]),
  },
  {
    label: "Tous les jours",
    ranges: makeRanges(Object.values(PaidDayOfWeek)),
  },
];

const paidTimeRangeSchema = z
  .object({
    day: z.nativeEnum(PaidDayOfWeek),
    startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Heure invalide (format HH:mm, 00:00–23:59)"),
    endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Heure invalide (format HH:mm, 00:00–23:59)"),
  })
  .refine((data) => data.startTime < data.endTime, {
    message: "L'heure de début doit être avant l'heure de fin",
    path: ["endTime"],
  });

const pricingConfigurationSchema = z.object({
  generationMode: generationModeSchema,
  roundingRules: roundingRulesSchema,
  anchors: z.array(pricingPointSchema).optional(),
});

export const MINUTES_PER_DAY = 1440;

const freeSessionSchema = z.object({
  minutes: z
    .number()
    .int("Les minutes gratuites doivent être un nombre entier")
    .min(1, "Au moins 1 minute gratuite est requise"),
  periodMinutes: z
    .number()
    .int("La période doit être un nombre entier de minutes")
    .min(2, "La période doit être d'au moins 2 minutes")
    .optional(),
  windowType: z.enum(FreeSessionWindowTypeEnum).optional(),
});

export type FreeSessionFormValues = z.infer<typeof freeSessionSchema>;

export const providerParkingSchema = z.object({
  provider: z.enum(TicketProviderEnum).optional(),
  requiresRight: z.boolean().optional(),
  easyparkAreaNo: z.number().int().optional(),
  extensoValidityArea: z.string().optional(),
  extensoProfile: z.string().optional(),
  polyconseilZone: z.string().optional(),
  polyconseilTariffCode: z.string().optional(),
  polyconseilPricingCategory: z.string().optional(),
  prestoPlatformUrbanParkingSiteId: z.string().optional(),
  prestoPlatformTariffType: z.string().optional(),
  prestoPlatformPointOfSale: z.string().optional(),
  prestoPlatformParkingPermits: z.array(z.string()).optional(),
});

export type ProviderParkingFormValues = z.infer<typeof providerParkingSchema>;

const providerParkingHasValue = (pp: ProviderParkingFormValues | undefined): boolean => {
  if (!pp?.provider) return false;
  // requiresRight is matched against the provider profile/zone, so it forces them to be set
  if (pp.requiresRight) return true;
  switch (pp.provider) {
    case TicketProviderEnum.Easypark:
      return pp.easyparkAreaNo != null;
    case TicketProviderEnum.Extenso:
    case TicketProviderEnum.ExtensoE2:
      return !!pp.extensoValidityArea?.trim() || !!pp.extensoProfile?.trim();
    case TicketProviderEnum.Polyconseil:
      return (
        !!pp.polyconseilZone?.trim() || !!pp.polyconseilTariffCode?.trim() || !!pp.polyconseilPricingCategory?.trim()
      );
    case TicketProviderEnum.PrestoPlatform:
      return (
        !!pp.prestoPlatformUrbanParkingSiteId?.trim() ||
        !!pp.prestoPlatformTariffType?.trim() ||
        !!pp.prestoPlatformPointOfSale?.trim() ||
        !!pp.prestoPlatformParkingPermits?.length
      );
    default:
      return false;
  }
};

export const parkingRateSchema = z
  .object({
    name: multilingualFieldSchema,
    detail: multilingualFieldSchema,
    zoneId: z.string().min(1, "Une zone doit être sélectionnée"),
    startDate: z.date({
      error: (issue) => (issue.input === undefined ? "La date de début est requise" : "La date de début est invalide"),
    }),
    endDate: z.date().nullable().optional(),
    shortcuts: z.array(z.object({ duration: z.number(), unit: z.enum(AnchorUnitEnum) })).optional(),
    freeMonths: z
      .array(
        z
          .number()
          .int("Les mois gratuits doivent être des nombres entiers")
          .min(1, "Les mois gratuits doivent être compris entre 1 et 12")
          .max(12, "Les mois gratuits doivent être compris entre 1 et 12"),
      )
      .optional(),
    freeOnHolidays: z.boolean().optional(),
    freeSession: freeSessionSchema.optional(),
    extendable: z.boolean().optional(),
    stoppable: z.boolean().optional(),
    rateAmount: z.number().min(0, "Le tarif doit être supérieur ou égal à 0"),
    rateUnit: z.string().min(1, "L'unité de tarif est requise"),
    userProfile: z.string().min(1, "Le profil utilisateur est requis"),
    feeConfigId: z.string().nullable().optional(),
    pricingConfiguration: pricingConfigurationSchema.optional(),
    paidTimeRanges: z.array(paidTimeRangeSchema).optional(),
    rateType: z.enum(RateTypeEnum).optional(),
    providerParking: providerParkingSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.rateType === RateTypeEnum.LONG) {
      const anchors = data.pricingConfiguration?.anchors ?? [];
      if (anchors.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "Au moins un point d'ancrage est requis pour une tarification longue durée",
          path: ["pricingConfiguration"],
        });
      } else if (anchors.some((a) => !a.unit)) {
        ctx.addIssue({
          code: "custom",
          message: "Chaque ligne doit avoir une unité (jour / semaine / mois)",
          path: ["pricingConfiguration"],
        });
      }
    }

    const fs = data.freeSession;
    if (fs) {
      if (data.rateType === RateTypeEnum.LONG) {
        ctx.addIssue({
          code: "custom",
          message: "La session gratuite n'est disponible que pour les tarifications courte durée",
          path: ["freeSession"],
        });
      }
      if (fs.windowType && fs.periodMinutes == null) {
        ctx.addIssue({
          code: "custom",
          message: "Une période est requise pour limiter la session gratuite",
          path: ["freeSession", "periodMinutes"],
        });
      }
      if (fs.periodMinutes != null && fs.periodMinutes <= fs.minutes) {
        ctx.addIssue({
          code: "custom",
          message: `La période (${fs.periodMinutes} min) doit dépasser les minutes gratuites (${fs.minutes} min)`,
          path: ["freeSession", "periodMinutes"],
        });
      }
      if (
        fs.windowType === FreeSessionWindowTypeEnum.Calendar &&
        fs.periodMinutes != null &&
        MINUTES_PER_DAY % fs.periodMinutes !== 0
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Une période calendaire doit diviser 24 h exactement (ex. 360, 720 ou 1440 min)",
          path: ["freeSession", "periodMinutes"],
        });
      }
      const anchors = data.pricingConfiguration?.anchors ?? [];
      if (anchors.some((a) => a.unit && a.unit !== AnchorUnitEnum.MIN)) {
        ctx.addIssue({
          code: "custom",
          message: "La session gratuite requiert des points d'ancrage exprimés en minutes",
          path: ["freeSession"],
        });
      } else {
        const maxStayMinutes = anchors.reduce((max, a) => Math.max(max, a.duration), 0);
        if (maxStayMinutes > 0 && fs.minutes > maxStayMinutes) {
          ctx.addIssue({
            code: "custom",
            message: `Les minutes gratuites (${fs.minutes}) ne doivent pas dépasser la durée maximale de stationnement (${maxStayMinutes} min)`,
            path: ["freeSession", "minutes"],
          });
        }
      }
    }

    const pp = data.providerParking;
    if (pp?.provider && providerParkingHasValue(pp)) {
      const requireField = (missing: boolean, field: string, message: string) => {
        if (missing) ctx.addIssue({ code: "custom", message, path: ["providerParking", field] });
      };
      switch (pp.provider) {
        case TicketProviderEnum.Easypark:
          requireField(pp.easyparkAreaNo == null, "easyparkAreaNo", "La zone EasyPark est requise");
          break;
        case TicketProviderEnum.Extenso:
        case TicketProviderEnum.ExtensoE2:
          requireField(!pp.extensoValidityArea?.trim(), "extensoValidityArea", "La zone de validité est requise");
          break;
        case TicketProviderEnum.Polyconseil:
          requireField(!pp.polyconseilZone?.trim(), "polyconseilZone", "La zone est requise");
          requireField(!pp.polyconseilTariffCode?.trim(), "polyconseilTariffCode", "Le code tarifaire est requis");
          requireField(
            !pp.polyconseilPricingCategory?.trim(),
            "polyconseilPricingCategory",
            "La catégorie tarifaire est requise",
          );
          break;
        case TicketProviderEnum.PrestoPlatform:
          requireField(
            !pp.prestoPlatformUrbanParkingSiteId?.trim(),
            "prestoPlatformUrbanParkingSiteId",
            "L'identifiant du site de stationnement est requis",
          );
          break;
      }
    }
  });

export type ParkingRateFormValues = z.infer<typeof parkingRateSchema>;

export const buildProviderParking = (pp: ProviderParkingFormValues | undefined): ProviderParking | undefined => {
  if (!pp?.provider || !providerParkingHasValue(pp)) return undefined;
  const base = pp.requiresRight ? { requiresRight: true as const } : {};
  switch (pp.provider) {
    case TicketProviderEnum.Easypark:
      return pp.easyparkAreaNo == null
        ? undefined
        : { provider: pp.provider, easyparkAreaNo: pp.easyparkAreaNo, ...base };
    case TicketProviderEnum.Extenso:
    case TicketProviderEnum.ExtensoE2: {
      if (!pp.extensoValidityArea?.trim()) return undefined;
      const extenso = {
        extensoValidityArea: pp.extensoValidityArea,
        ...(pp.extensoProfile?.trim() ? { extensoProfile: pp.extensoProfile } : {}),
        ...base,
      };
      return pp.provider === TicketProviderEnum.Extenso
        ? { provider: TicketProviderEnum.Extenso, ...extenso }
        : { provider: TicketProviderEnum.ExtensoE2, ...extenso };
    }
    case TicketProviderEnum.Polyconseil:
      if (!pp.polyconseilZone?.trim() || !pp.polyconseilTariffCode?.trim() || !pp.polyconseilPricingCategory?.trim()) {
        return undefined;
      }
      return {
        provider: pp.provider,
        polyconseilZone: pp.polyconseilZone,
        polyconseilTariffCode: pp.polyconseilTariffCode,
        polyconseilPricingCategory: pp.polyconseilPricingCategory,
        ...base,
      };
    case TicketProviderEnum.PrestoPlatform:
      if (!pp.prestoPlatformUrbanParkingSiteId?.trim()) return undefined;
      return {
        provider: pp.provider,
        prestoPlatformUrbanParkingSiteId: pp.prestoPlatformUrbanParkingSiteId,
        ...(pp.prestoPlatformTariffType?.trim() ? { prestoPlatformTariffType: pp.prestoPlatformTariffType } : {}),
        ...(pp.prestoPlatformPointOfSale?.trim() ? { prestoPlatformPointOfSale: pp.prestoPlatformPointOfSale } : {}),
        ...(pp.prestoPlatformParkingPermits?.length
          ? { prestoPlatformParkingPermits: pp.prestoPlatformParkingPermits }
          : {}),
        ...base,
      };
    default:
      return undefined;
  }
};

export const validityAreaSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  eTicketId: z.number().int().optional(),
});

export const easyparkAreaSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  areaNo: z.number().int().optional(),
});

// Mirrors clients-api's EasyPark rule: `code` is the hub subdomain, a bare lowercase DNS label.
const EASYPARK_CODE_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const EASYPARK_COUNTRY_CODE_REGEX = /^[A-Z]{2}$/;

export const EASYPARK_CODE_HELP =
  "Le sous-domaine SmartHUB fourni par EasyPark pour cet environnement (le « xxx » dans xxx.parkinghub.net). Lettres minuscules, chiffres et tirets uniquement.";

const validateEasyparkCode = (value: string | undefined, ctx: z.RefinementCtx) => {
  if (value?.trim() && !EASYPARK_CODE_REGEX.test(value)) {
    ctx.addIssue({
      code: "custom",
      message: "Lettres minuscules, chiffres et tirets uniquement, sans tiret en début ou en fin",
      path: ["code"],
    });
  }
};

const ticketProviderSchema = z
  .object({
    provider: z.enum(TicketProviderEnum).optional(),
    code: z.string().optional(),
    // easypark
    easyparkAreas: z.array(easyparkAreaSchema).optional(),
    easyparkAreaCountryCode: z.string().optional(),
    // extenso only
    extensoETicketUrl: z.url("L'URL doit être valide").optional(),
    extensoETicketPassword: z.string().optional(),
    // extenso + extenso_e2
    extensoValidityAreas: z.array(validityAreaSchema).optional(),
    extensoProfiles: z.array(z.string()).optional(),
    extensoDefaultProfile: z.string().optional(),
    // polyconseil
    polyconseilZones: z.array(z.string()).optional(),
    polyconseilSubscriptionPlans: z.array(z.string()).optional(),
    polyconseilPricingCategories: z.array(z.string()).optional(),
    polyconseilTariffCodes: z.array(z.string()).optional(),
    polyconseilValidityAreas: z.array(z.string()).optional(),
    // presto_platform
    prestoPlatformPointsOfSale: z.array(z.string()).optional(),
    // ui-only flag, stripped from the payload before saving
    passwordAlreadySet: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.provider) return;
    if (!data.code?.trim()) {
      ctx.addIssue({ code: "custom", message: "Le code est requis", path: ["code"] });
    }

    const isExtenso = data.provider === TicketProviderEnum.Extenso;
    const isExtensoE2 = data.provider === TicketProviderEnum.ExtensoE2;

    if (data.provider === TicketProviderEnum.Easypark) {
      validateEasyparkCode(data.code, ctx);
      if (!EASYPARK_COUNTRY_CODE_REGEX.test(data.easyparkAreaCountryCode ?? "")) {
        ctx.addIssue({
          code: "custom",
          message: "Code pays à 2 lettres majuscules requis (ex. PT)",
          path: ["easyparkAreaCountryCode"],
        });
      }
      const areas = data.easyparkAreas ?? [];
      const areaNames = areas.map((area) => area.name.trim());
      if (new Set(areaNames).size !== areaNames.length) {
        ctx.addIssue({
          code: "custom",
          message: "Les noms des zones doivent être uniques",
          path: ["easyparkAreas"],
        });
      }
      if (areas.some((area) => area.areaNo === undefined || !Number.isInteger(area.areaNo))) {
        ctx.addIssue({
          code: "custom",
          message: "Chaque zone doit avoir un numéro de zone EasyPark",
          path: ["easyparkAreas"],
        });
      } else if (new Set(areas.map((area) => area.areaNo)).size !== areas.length) {
        ctx.addIssue({
          code: "custom",
          message: "Les numéros de zone doivent être uniques",
          path: ["easyparkAreas"],
        });
      }
    }

    if (isExtenso) {
      if (!data.extensoETicketUrl) {
        ctx.addIssue({ code: "custom", message: "L'URL eTicket est requise", path: ["extensoETicketUrl"] });
      }
      if (!data.passwordAlreadySet && !data.extensoETicketPassword?.trim()) {
        ctx.addIssue({ code: "custom", message: "Le mot de passe est requis", path: ["extensoETicketPassword"] });
      }
    }

    if (isExtenso || isExtensoE2) {
      const areas = data.extensoValidityAreas ?? [];
      const names = areas.map((area) => area.name.trim());
      if (new Set(names).size !== names.length) {
        ctx.addIssue({
          code: "custom",
          message: "Les noms des zones de validité doivent être uniques",
          path: ["extensoValidityAreas"],
        });
      }
      if (isExtenso && areas.some((area) => area.eTicketId === undefined || !Number.isInteger(area.eTicketId))) {
        ctx.addIssue({
          code: "custom",
          message: "Chaque zone de validité doit avoir un ID eTicket",
          path: ["extensoValidityAreas"],
        });
      }
      const profiles = data.extensoProfiles ?? [];
      if (data.extensoDefaultProfile && !profiles.includes(data.extensoDefaultProfile)) {
        ctx.addIssue({
          code: "custom",
          message: "Le profil par défaut doit faire partie des profils",
          path: ["extensoDefaultProfile"],
        });
      }
    }
  });

const rightsProviderSchema = z
  .object({
    provider: z.enum(RightsProviderEnum).optional(),
    code: z.string().optional(),
    // extenso
    extensoProfiles: z.array(z.string()).optional(),
    extensoValidityAreas: z.array(z.string()).optional(),
    // polyconseil
    polyconseilSubscriptionPlans: z.array(z.string()).optional(),
    polyconseilValidityAreas: z.array(z.string()).optional(),
    // presto_platform
    prestoPlatformUrbanParkingSiteIds: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.provider) return;
    if (!data.code?.trim()) {
      ctx.addIssue({ code: "custom", message: "Le code est requis", path: ["code"] });
    }
    const requireNonEmpty = (value: string[] | undefined, path: string, message: string) => {
      if (!value?.length) {
        ctx.addIssue({ code: "custom", message, path: [path] });
      }
    };
    if (data.provider === RightsProviderEnum.Easypark) {
      validateEasyparkCode(data.code, ctx);
    } else if (data.provider === RightsProviderEnum.Extenso) {
      requireNonEmpty(data.extensoProfiles, "extensoProfiles", "Au moins un profil est requis");
      requireNonEmpty(data.extensoValidityAreas, "extensoValidityAreas", "Au moins une zone de validité est requise");
    } else if (data.provider === RightsProviderEnum.Polyconseil) {
      requireNonEmpty(
        data.polyconseilSubscriptionPlans,
        "polyconseilSubscriptionPlans",
        "Au moins un plan d'abonnement est requis",
      );
      requireNonEmpty(
        data.polyconseilValidityAreas,
        "polyconseilValidityAreas",
        "Au moins une zone de validité est requise",
      );
    } else if (data.provider === RightsProviderEnum.PrestoPlatform) {
      requireNonEmpty(
        data.prestoPlatformUrbanParkingSiteIds,
        "prestoPlatformUrbanParkingSiteIds",
        "Au moins un identifiant de site est requis",
      );
    }
  });

export const cityConfigSchema = z.object({
  ticketProvider: ticketProviderSchema.optional(),
  rightsProvider: rightsProviderSchema.optional(),
});

export type CityConfigFormValues = z.infer<typeof cityConfigSchema>;

/**
 * Array-typed fields per provider family. The backend requires these as present
 * arrays, so they must be backfilled to [] when loading a config that predates
 * them — otherwise an untouched field submits as a missing key and is rejected.
 */
export const TICKET_PROVIDER_ARRAY_FIELDS = [
  "easyparkAreas",
  "extensoValidityAreas",
  "extensoProfiles",
  "polyconseilZones",
  "polyconseilSubscriptionPlans",
  "polyconseilPricingCategories",
  "polyconseilTariffCodes",
  "polyconseilValidityAreas",
  "prestoPlatformPointsOfSale",
] as const;

export const RIGHTS_PROVIDER_ARRAY_FIELDS = [
  "extensoProfiles",
  "extensoValidityAreas",
  "polyconseilSubscriptionPlans",
  "polyconseilValidityAreas",
  "prestoPlatformUrbanParkingSiteIds",
] as const;

/** Different GeoJsonEditModes depending on app mode */
export const parkingPaymentGeoJsonLayerModes: Record<ParkingPaymentModeType, PaymentModeConstructor> = {
  [ParkingPaymentModeType.View]: ViewMode,
  [ParkingPaymentModeType.AddParkingPayment]: ViewMode,
  [ParkingPaymentModeType.EditParkingPayment]: ViewMode,
  [ParkingPaymentModeType.ManagePaymentZoneArea]: ViewMode,
  [ParkingPaymentModeType.AddPaymentZoneArea]: DrawPolygonMode,
  [ParkingPaymentModeType.EditPaymentZoneArea]: ModifyMode,
  [ParkingPaymentModeType.AddParkingRate]: ViewMode,
  [ParkingPaymentModeType.CityConfig]: ViewMode,
  [ParkingPaymentModeType.ManageFeeConfigs]: ViewMode,
};

export const paymentZoneAreaMode: ParkingPaymentModeType[] = [
  ParkingPaymentModeType.AddPaymentZoneArea,
  ParkingPaymentModeType.EditPaymentZoneArea,
];

export const parkingPaymentMode: ParkingPaymentModeType[] = [
  ParkingPaymentModeType.AddParkingPayment,
  ParkingPaymentModeType.EditParkingPayment,
];

export type PaymentZoneColorOption = {
  name: string;
  rgba: [number, number, number, number];
};

export const PAYMENT_ZONE_COLORS: readonly PaymentZoneColorOption[] = [
  { name: "Bleu", rgba: [37, 99, 235, 255] },
  { name: "Rouge", rgba: [220, 38, 38, 255] },
  { name: "Vert", rgba: [22, 163, 74, 255] },
  { name: "Ambre", rgba: [245, 158, 11, 255] },
  { name: "Violet", rgba: [139, 92, 246, 255] },
  { name: "Rose", rgba: [236, 72, 153, 255] },
  { name: "Turquoise", rgba: [20, 184, 166, 255] },
  { name: "Orange", rgba: [249, 115, 22, 255] },
  { name: "Indigo", rgba: [99, 102, 241, 255] },
  { name: "Lime", rgba: [132, 204, 22, 255] },
  { name: "Cyan", rgba: [6, 182, 212, 255] },
  { name: "Jaune", rgba: [234, 179, 8, 255] },
  { name: "Magenta", rgba: [219, 39, 119, 255] },
  { name: "Bleu ciel", rgba: [14, 165, 233, 255] },
  { name: "Pourpre", rgba: [168, 85, 247, 255] },
  { name: "Corail", rgba: [251, 146, 60, 255] },
  { name: "Émeraude", rgba: [16, 185, 129, 255] },
  { name: "Fuchsia", rgba: [217, 70, 239, 255] },
  { name: "Bleu marine", rgba: [30, 64, 175, 255] },
  { name: "Vert olive", rgba: [132, 204, 22, 255] },
  { name: "Marron", rgba: [120, 53, 15, 255] },
] as const;

export const DEFAULT_COLOR_RGBA: [number, number, number, number] = [54, 240, 171, 255];

export const applyOpacityToRgba = (
  rgba?: [number, number, number, number],
  opacity = 0.3,
): [number, number, number, number] => {
  const [r, g, b, a = 255] = rgba ?? DEFAULT_COLOR_RGBA;
  return [r, g, b, opacity === 1 ? a : Math.round(opacity * 255)];
};

export const formatPolygonCount = (zone: PaymentZoneArea) => {
  const count = zone.geometry.coordinates.length ?? 0;
  if (!count) return "";
  return `${count} ${count > 1 ? "polygones" : "polygone"}`;
};

export const zoneCount = (paymentZones: PaymentZoneArea[]) => {
  const total = paymentZones.length;
  return total === 0 ? "Aucune zone" : `${total} ${total > 1 ? "zones" : "zone"}`;
};

export const multiPolygonToPolygons = (multiPolygon: MultiPolygon): Polygon[] =>
  multiPolygon.coordinates.map((coordinates) => ({
    type: "Polygon",
    coordinates,
  }));

export const getPolygonsFromGeometry = (geometry: Polygon | MultiPolygon): Polygon[] => {
  if (geometry.type === "Polygon") return [geometry];
  return multiPolygonToPolygons(geometry);
};

export const polygonsToMultiPolygon = (polygons: Polygon[]): MultiPolygon => ({
  type: "MultiPolygon",
  coordinates: polygons.map((polygon) => polygon.coordinates),
});

export const createDraftState = (
  featureCount: number,
  mode: "create" | "edit",
  options: {
    name?: string;
    color?: [number, number, number, number];
    zoneId?: string;
    originalGeometry?: MultiPolygon;
    originalZone?: PaymentZoneArea;
  } = {},
): DraftState => ({
  mode,
  status: "form",
  name: options.name ?? "",
  color: options.color ?? PAYMENT_ZONE_COLORS[mode === "create" ? 2 : 0]?.rgba,
  baseFeatureCount: featureCount,
  zoneId: options.zoneId,
  originalGeometry: options.originalGeometry,
  originalZone: options.originalZone,
});

export const TICKET_PROVIDER_OPTIONS: { value: TicketProviderEnum; label: string }[] = [
  { value: TicketProviderEnum.Easypark, label: "EasyPark" },
  { value: TicketProviderEnum.Extenso, label: "Extenso" },
  { value: TicketProviderEnum.ExtensoE2, label: "Extenso E2" },
  { value: TicketProviderEnum.Polyconseil, label: "Polyconseil" },
  { value: TicketProviderEnum.PrestoPlatform, label: "PrestoPlatform" },
];

export const RIGHTS_PROVIDER_OPTIONS: { value: RightsProviderEnum; label: string }[] = [
  { value: RightsProviderEnum.Easypark, label: "EasyPark" },
  { value: RightsProviderEnum.Extenso, label: "Extenso" },
  { value: RightsProviderEnum.Polyconseil, label: "Polyconseil" },
  { value: RightsProviderEnum.PrestoPlatform, label: "PrestoPlatform" },
];

export const toastSuccess = (msg: string) => toast.success(msg);
export const toastError = (msg: string) => toast.error(msg);

export const overlapErrorMessage = (
  overlapping: { properties?: { name?: string } | null },
  subject = "Ce polygone",
): string => {
  const name = overlapping.properties?.name;
  return name ? `${subject} chevauche la zone "${name}".` : `${subject} chevauche une zone existante.`;
};
